import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { alpha, useTheme } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { paths } from "src/paths";
import { HomeGlucoseGraphAnimation } from "./home-glucose-graph-animation";

const TOP_NAV_HEIGHT = 64;

interface HomeHeroProps {
  handleScroll: (pagePart: "features" | "pricing") => void;
}

export const HomeHero: FC<HomeHeroProps> = ({ handleScroll }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        minHeight: { xs: "auto", md: `calc(100vh - ${TOP_NAV_HEIGHT}px)` },
        display: "flex",
        alignItems: "center",
        pt: { xs: 12, md: TOP_NAV_HEIGHT / 8 },
        pb: { xs: 8, md: 4 },
      }}
    >
      {/* <HomeGlucoseGraphAnimation /> */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          <Grid xs={12} md={7} order={{ xs: 2, md: 1 }}>
            <Stack
              spacing={3}
              sx={{
                maxWidth: 560,
                mx: { xs: "auto", md: 0 },
                justifyContent: "center",
              }}
            >
              <Typography
                component="h1"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  fontSize: { xs: "2.125rem", md: "3.25rem" },
                  letterSpacing: "-0.03em",
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                {t("Setting up your open-source loop made easy")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.7,
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                {t("Remote medical guidance to get the most out of your loop.")}
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "center", sm: "center" }}
                sx={{
                  justifyContent: { xs: "center", md: "flex-start" },
                  pt: 1,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  href="https://cal.com/team/opensourcelinic/medical-coaching"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="large"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3.5,
                    py: 1.25,
                    borderRadius: 2,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.85),
                      boxShadow: "none",
                    },
                  }}
                >
                  {t("Start coaching")}
                </Button>
                <Button
                  href={paths.auth.firebase.login}
                  variant="outlined"
                  size="large"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3.5,
                    py: 1.25,
                    borderRadius: 2,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.text.primary,
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  {t("Setup your loop")}
                </Button>
                <Button
                  onClick={() => handleScroll?.("features")}
                  variant="text"
                  size="large"
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    px: 1.5,
                    py: 1.25,
                    minWidth: "auto",
                    color: theme.palette.text.secondary,
                    letterSpacing: "0.01em",
                    "& .MuiButton-endIcon": {
                      ml: 0.5,
                      transition: "transform 0.2s ease",
                    },
                    "&:hover": {
                      color: theme.palette.primary.main,
                      backgroundColor: "transparent",
                      "& .MuiButton-endIcon": {
                        transform: "translateY(2px)",
                      },
                    },
                  }}
                >
                  {t("Read more")}
                </Button>
              </Stack>
            </Stack>
          </Grid>
          <Grid xs={12} md={5} order={{ xs: 1, md: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
                alignItems: "center",
              }}
            >
              <Box
                component="img"
                src="/assets/osc-logo-transparent.png"
                alt="Opensource.clinic logo"
                width="800"
                height="800"
                loading="eager"
                sx={{
                  display: "block",
                  width: "120%",
                  height: "auto",
                  objectFit: "contain",
                  // Knock out solid white so the haze/graph shows through
                  mixBlendMode: "multiply",
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
